import * as moment from "moment-timezone";
import * as React from "react";
import styled, { css } from "styled-components";
import { useHistory } from "react-router-dom";
import { useMappedState } from "redux-react-hook";

import { Button } from "src/library/components/Button";
import { OrderDayPicker } from "src/library/components/DayPicker";
import { Text } from "src/library/components/Text";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { Icon } from "src/library/components/Icon";
import { PaginatedData } from "src/library/utils";
import { Icons } from "src/library/components/Icon/types";
import { getOrderStatusIdFromCode } from "src/core/orders/utils/orderStatus";
import {
  ListOrdersByDropParams,
  ordersApi,
  IOrderStatus,
  IOrder
} from "src/core/orders/api/orders.api";
import {
  ISchedule,
  ScheduleType
} from "src/core/schedules/api/schedules.api";
import { IDrop } from "src/core/drops/api/drops.api";
import {
  IError,
  IListErrorData
} from "src/core/errors/api/errors.api";
import { IStore } from "src/core/shared/store";

interface IScheduleCalendarProps {
  schedule: ISchedule | null;
  scheduleErrors?: Array<IError<IListErrorData>>;
  drop: IDrop | null;
  terminology: ITerminology;
  orderStatuses: IOrderStatus[];
}

export const ScheduleCalendar: React.ReactFCWithChildren<IScheduleCalendarProps> = ({
  schedule, drop, scheduleErrors, terminology, orderStatuses
}) => {
  const history = useHistory();
  const dateFrom = new Date();
  const [ ordersResponse, getOrders ] = ordersApi<{ orders: PaginatedData<IOrder> }>("LIST_DROP_ORDERS");
  const [ orderDates, setOrderDates ] = React.useState<Array<{ id: number; date: Date }>>([]);
  const { scheduleSiteSettings } = useMappedState(({ global }: IStore) => ({ scheduleSiteSettings: global.settings?.siteSettings?.schedule }));
  const cancelledOrderStatusId = React.useMemo(() => getOrderStatusIdFromCode(orderStatuses, "cancelled"), [ orderStatuses ]);
  const provisionalOrderStatusId = React.useMemo(() => getOrderStatusIdFromCode(orderStatuses, "provisional"), [ orderStatuses ]);

  const [ currentMonth, setCurrentMonth ] = React.useState<{
    month: number;
    year: number;
  }>({
    month: dateFrom.getMonth(),
    year: dateFrom.getFullYear()
  });

  const scheduleGeneralWarning: string | undefined = React.useMemo(() => {
    if (schedule?.type === ScheduleType.Ongoing && scheduleSiteSettings?.extendLength) {
      return `This is an ongoing schedule. Orders are placed ${scheduleSiteSettings.extendLength} days ahead of today.`;
    }

    return;
  }, [ schedule?.type, scheduleSiteSettings?.extendLength ]);

  const getCalendarOrders = React.useCallback(() => {
    if (currentMonth && schedule && drop) {
      getOrders<ListOrdersByDropParams>({
        pagination: {
          offset: 0,
          page: 0,
          sort: [ [ "id", "ASC" ] ],
          limit: 100
        },
        filters: {
          type: { $ne: "bulk" },
          $or: [
            {
              scheduleId: schedule.id,
              orderDeliveryDate: {
                $between: [
                  moment
                    .utc()
                    .month(currentMonth.month)
                    .year(currentMonth.year)
                    .startOf("month")
                    .toDate(),
                  moment
                    .utc()
                    .month(currentMonth.month)
                    .year(currentMonth.year)
                    .endOf("month")
                ]
              }
            },
            {
              branchId: drop.id,
              orderDeliveryDate: {
                $in: (scheduleErrors || []).map(error =>
                  moment
                    .utc(error.data!.deliveryDate)
                    .local()
                    .format("YYYY-MM-DD")
                )
              }
            }
          ]
        },
        dropId: drop.id
      });
    }
  }, [
    currentMonth,
    drop,
    schedule
  ]);

  React.useEffect(() => {
    getCalendarOrders();
  }, [ currentMonth, schedule ]);

  React.useEffect(() => {
    if (ordersResponse.data) {
      const newOrderDates: Array<{ id: number; date: Date }> = [];

      if (ordersResponse.data.orders.result) {
        for (const order of ordersResponse.data.orders.result) {
          if (order.orderStatus) {
            newOrderDates.push({
              id: order.id,
              date: order.orderDeliveryDate as Date
            });
          }
        }

        setOrderDates(newOrderDates);
      }
    }
  }, [ ordersResponse ]);

  const handleRouteToOrder = React.useCallback((date?: Date) => {
    if (date) {
      const order = orderDates.find(order => moment.utc(order.date).isSame(date, "date"));

      if (order) {
        history.push(`/orders/${order.id}`);
      } else {
        // push to orders with date query
        history.push(`/orders?filters=deliveryDate:${moment.utc(date).format("YYYY-MM-DD")}`, { ordersViewType: "table" });
      }
    } else {
      history.push("/orders", { ordersViewType: "table" });
    }
  }, [ history, orderDates ]);

  const warningDates = React.useMemo(() => {
    if (!scheduleErrors) {
      return [];
    }

    return scheduleErrors
      .filter(error => error.code === "standing-order-error")
      .map((error, i) => {
        return {
          component: (
            <WarningComponent
              key={`${i}-warning`}
              scheduleError
            >
              {error.data && (
                <>
                  <WarningText>
                    <Icon
                      size={16}
                      icon={Icons.ERRORCIRCLE}
                      $fillColor="error"
                    />

                    {" "}

                    {error.data.reason}
                  </WarningText>

                  <Button
                    $buttonType="secondary"
                    onClick={() => handleRouteToOrder(error.data && error.data.deliveryDate)}
                  >
                    {`View this ${terminology.order.singular.text}`}
                  </Button>
                </>
              )}
            </WarningComponent>
          ),

          date: error.data ? moment.utc(error.data.deliveryDate).toDate() : new Date()
        };
      });
  }, [
    handleRouteToOrder,
    scheduleErrors,
    terminology.order.singular.text
  ]);

  // Orders that are no longer effected by schedule changes
  const detachedOrders = React.useMemo(() => {
    if (ordersResponse.data?.orders.result) {
      return ordersResponse.data.orders.result.filter(order => order.type === "adhoc");
    } else {
      return [];
    }
  }, [ ordersResponse.data?.orders.result ]);

  // List of order dates that won't be effected by schedule changes
  const disabledOrders = React.useMemo(() => {
    return detachedOrders.map(order => {
      if (order.orderStatusId === cancelledOrderStatusId) {
        return {
          date: order.orderDeliveryDate as Date,
          component: (
            <WarningComponent scheduleError={false}>
              <>
                <WarningText>
                  <Text>
                    {`This ${terminology.order.singular.text} was cancelled and is no longer effected by changes to the ${terminology.schedule.singular.text}.`}
                  </Text>
                </WarningText>

                <Button
                  $buttonType="secondary"
                  onClick={() => handleRouteToOrder(order.orderDeliveryDate ? moment.utc(order.orderDeliveryDate).toDate() : undefined)}
                >
                  {`View this ${terminology.order.singular.text}`}
                </Button>
              </>
            </WarningComponent>
          )
        };
      } else if (order.orderStatusId === provisionalOrderStatusId) {
        return {
          date: order.orderDeliveryDate as Date,
          component: (
            <WarningComponent scheduleError={false}>
              <>
                <WarningText>
                  <Text>
                    {`This ${terminology.order.singular.text} was manually changed and is no longer effected by changes to the ${terminology.schedule.singular.text}.`}
                  </Text>
                </WarningText>

                <Button
                  $buttonType="secondary"
                  onClick={() => handleRouteToOrder(order.orderDeliveryDate ? moment.utc(order.orderDeliveryDate).toDate() : undefined)}
                >
                  {`View this ${terminology.order.singular.text}`}
                </Button>
              </>
            </WarningComponent>
          )
        };
      }

      return { date: order.orderDeliveryDate as Date };
    });
  }, [
    cancelledOrderStatusId,
    detachedOrders,
    handleRouteToOrder,
    provisionalOrderStatusId,
    terminology.order.singular.text,
    terminology.schedule.singular
  ]);

  const disabled = React.useCallback((date: Date) => {
    if (warningDates.find(day => moment.utc(day.date).isSame(moment.utc(date), "date"))) {
      return { isDisabled: false };
    } else if (orderDates.find(day => moment.utc(day.date).isSame(moment.utc(date), "date"))) {
      return { isDisabled: false };
    }

    return {
      isDisabled: true,
      component: (
        <Text>
          {`No ${terminology.schedule.plural.text} on this day`}
        </Text>
      )
    };
  }, [
    orderDates,
    terminology.schedule.plural.text,
    warningDates
  ]);

  return (
    <Wrapper>
      <Text
        className="calendar-title"
        as="h3"
      >
        Calendar
      </Text>

      <OrderDayPicker
        theme="light"
        loading={ordersResponse.pending}
        orderDates={orderDates}
        date={dateFrom}
        handleDateChange={() => {
          // no action needed
        }}
        modifiersStyles={{
          warning: {
            background: "#FF7285",
            color: "white"
          }
        }}
        handleMonthChange={(month: Date) => {
          setCurrentMonth({
            month: month.getMonth(),
            year: month.getFullYear()
          });
        }}
        disabled={disabled}
        fromDate={moment
          .utc()
          .subtract(2, "years")
          .toDate()}
        warningDates={warningDates}
        withOrdersComponent
        handleRouteToOrder={handleRouteToOrder}
        toDate={moment
          .utc()
          .add(2, "years")
          .toDate()}
        generalWarning={scheduleGeneralWarning}
        disabledDates={disabledOrders}
      />
    </Wrapper>
  );
};

const WarningText = styled(Text)`
  display: flex;
`;

const Wrapper = styled.div`
  ${({ theme }) => css`
  
    .calendar-title {
      margin-left: ${theme.spacing.margins.md};
      padding-bottom: ${theme.spacing.padding.sm};
      margin-bottom: ${theme.spacing.padding.sm};
      border-bottom: ${theme.colors.lightGrey} 1px solid;
    }
    .calendar-wrapper {
      width: 100%;
      
      .DayPicker-Day {
        height: 45px;
        width: 45px;

        &.DayPicker-Day--disabled.DayPicker-Day--withOrders {
          background: ${theme.colors.warn};
          color: ${theme.colors.darkGrey};
        }
      }
      .DayPicker-Weekday {
        font-size: 14px;
        padding-top: 30px;
        font-weight: bold;
      }
    }
  `}
`;

const WarningComponent = styled.div<{scheduleError: boolean}>`
  ${({ theme, scheduleError }) => css`
    span {
      color: ${theme.colors[ scheduleError ? "error" : "warn" ]};
    }
    svg {
      color: ${theme.colors.error};
      margin-right: ${theme.spacing.margins.xs};
    }
    button {
      margin-top: ${theme.spacing.margins.md};
    }
  `}
`;
